import {BaseComponent} from './base-component.js';
import {customElement, property} from 'lit/decorators.js';
import {html, css, CSSResultGroup, TemplateResult, svg} from 'lit';

type InputType =
  | 'text'
  | 'password'
  | 'email'
  | 'number'
  | 'tel'
  | 'url'
  | 'search'
  | 'date'
  | 'time'
  | 'datetime-local'
  | 'month'
  | 'week'
  | 'color'
  | 'file'
  | 'range'
  | 'hidden'
  | 'checkbox'
  | 'radio'
  | 'submit'
  | 'reset'
  | 'button'
  | 'image';

@customElement('il-input')
export default class InputComponent extends BaseComponent {
  @property({type: String}) type: InputType = 'text';
  @property({type: String}) value?: string | number;
  @property({type: String}) label?: string | number;
  @property({type: String}) hint?: string | number;
  @property({type: String, attribute: 'error-message'}) errorMessage?: string;
  @property({type: String}) placeholder?: string | number;
  @property({type: Boolean}) disabled?: boolean;
  @property({type: Boolean, attribute: 'full-width', reflect: true}) fullWidth?: boolean = false;
  @property({type: String, attribute: 'input-prefix'}) inputPrefix?: string;
  @property({type: String, attribute: 'input-button-label'}) inputButtonLabel:
    | string
    | number = 'Submit';

  @property({type: Object}) onChange?: (option: string | number) => void;

  @property({type: Object}) onBlur?: (option: string | number) => void;

  @property({type: Object})
  onClickInputButton?: () => void;

  @property({type: String}) autocomplete?: string;

  /**
   * returns an internal id to be used on the input and label
   * gets the @id property passed from the glimmer component and adds so it is a unique ID
   *
   * if this component is used without glimmer, this should also work by passing the @id property
   * when calling the web component HTML tag, e.g.:
   * <iliad-input id="some-id"></iliad-input>
   */
  get internalId(): string {
    return `iliad-${this.id}`;
  }

  // prettier-ignore
  static override styles = [
    BaseComponent.styles,
    css`
      :host([full-width]) {
        width: 100%;
      }

      .il-input-container {
        display: flex;
        flex-direction: column;
        gap: var(--il-size-6);
      }

      .il-input-container.il-input-container--disabled .il-input__label,
      .il-input-container.il-input-container--disabled .il-input__hint,
      .il-input-container.il-input-container--disabled .il-input__input {
        opacity: 0.5;
      }

      .il-input-container .il-input__label {
        color: var(--il-color-neutrals-300);
        display: inline;
        font-size: var(--il-font-size-14);
        line-height: var(--il-font-line-height-150);
      }

      .il-input-container .il-input__hint {
        color: var(--il-color-neutrals-300);
        display: none;
        font-size: var(--il-font-size-12);
        line-height: var(--il-font-line-height-150);
        letter-spacing: var(--il-font-letter-spacing-1);
      }
      .il-input-container--has-hint .il-input__hint {
        display: inline-block;
      }

      .il-input-container .il-input__error-message {
        display: flex;
        align-items: flex-start;
        gap: var(--il-size-8);

        color: var(--il-color-warning);
        font-size: var(--il-font-size-14);
        line-height: var(--il-font-line-height-140);
      }

      .il-input-container .il-input__error-message svg {
        flex-shrink: 0;
      }

      .il-input-container .il-input {
        display: flex;
        height: 44px;
      }

      /* Remove stepper arrows from number inputs */
      /* Chrome, Safari, Edge, Firefox */
      .il-input-container .il-input .il-input__input[type=number]::-webkit-inner-spin-button,
      .il-input-container .il-input .il-input__input[type=number]::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }

      /* Firefox */
      .il-input-container .il-input .il-input__input[type=number] {
        -moz-appearance: textfield;
      }

      .il-input-container.il-input-container--has-error .il-input__input {
        border-color: var(--il-color-warning);
      }

      .il-input-container .il-input__input::placeholder {
        color: var(--il-color-neutrals-300);
      }

      .il-input-container .il-input__input:hover:not([disabled], :focus) {
        border-color: var(--il-color-neutrals-250);
      }

      .il-input-container .il-input__input:focus {
        background-color: var(--il-color-neutrals-0);
      }

      .il-input-container .il-input__input:focus, .il-input-container .il-input__input:focus-visible {
        border-color: var(--il-color-accent-200);
      }

      .il-input-container .il-input il-button::part(button) {
        padding: var(--il-size-12);
        height: 100%;
        border-radius: 0px var(--il-size-6) var(--il-size-6) 0px;
        margin-left: -1px;
      }

      .il-input-container .il-input__input {
        background: var(--il-color-neutrals-50);
        border-color: var(--il-color-neutrals-200);
        border-radius: var(--il-size-6);
        border-style: solid;
        border-width: 1px;
        border-radius: var(--il-size-6);
        color: var(--il-color-neutrals-350);
        display: flex;
        flex: auto;
        font-size: var(--il-font-size-16);
        outline: none;
        padding: var(--il-size-12);
      }

      .il-input-container .il-input__input.il-input__input--has-prefix {
        border-radius: 0px var(--il-size-6) var(--il-size-6) 0px;
      }

      .il-input-container .il-input__input.il-input__input--has-value {
        font-weight: var(--il-font-weight-bold);
      }

      .il-input-container .il-input__input.il-input__input--has-button {
        border-radius: var(--il-size-6) 0px 0px var(--il-size-6);
      }

      .il-input-container .il-input__input.il-input__input--has-prefix {
        border-radius: 0px var(--il-size-6) var(--il-size-6) 0px;
      }

      .il-input-container .il-input__input.il-input__input--has-prefix.il-input__input--has-button {
        border-radius: 0px;
        padding-left: 8px;
      }

      .il-input-container .il-input__prefix {
        background: var(--il-color-neutrals-50);
        border-radius: var(--il-size-6) 0px 0px var(--il-size-6);
        border: 1px solid var(--il-color-neutrals-200);
        border-right: none;
        color: var(--il-color-neutrals-300);
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: var(--il-font-size-16);
        outline: none;
        padding: var(--il-size-8) var(--il-size-12);
        gap: var(--il-size-8);
      }
    `,
  ] as CSSResultGroup

  override render() {
    return html`
      <div
        class="
          ${this.baseCSSClass}
          il-input-container
          ${this.disabled ? 'il-input-container--disabled' : ''}
          ${this.errorMessage ? 'il-input-container--has-error' : ''}
          ${this.hint ? 'il-input-container--has-hint' : ''}
        "
        data-test-iliad-input-container
      >
        <div class="il-input__label-container">
          <slot name="label-before" data-test-iliad-input-label-before></slot>
          ${this.renderLabel()}
          <slot name="label-after" data-test-iliad-input-label-after></slot>
        </div>

        <div class="il-input__hint">
          <slot name="hint" data-test-iliad-input-hint>
            ${this.hint}
          </slot>
        </div>

        <div class="il-input">
          ${this._renderInputPrefix()}
          <input
            id=${this.internalId}
            .value=${String(this.value ?? '')}
            type=${this.type}
            class="
              il-input__input
              ${this.value ? 'il-input__input--has-value' : ''}
              ${this.onClickInputButton ? 'il-input__input--has-button' : ''}
              ${this.inputPrefix ? 'il-input__input--has-prefix' : ''}
            "
            placeholder="${this.placeholder ?? ''}"
            autocomplete="${this.autocomplete ?? 'off'}"
            @input=${this._onChange}
            @blur=${this._onBlur}
            ?disabled=${this.disabled}
            data-test-iliad-input
          />

          ${this.renderInputButton()}
        </div>

        ${this.renderErrorMessage()}
      </div>
    `;
  }

  private renderLabel(): TemplateResult {
    if (this.label) {
      return html`<label
        for=${this.internalId}
        class="il-input__label"
        data-test-iliad-input-label
      >
        <slot name="label">${this.label}</slot>
      </label>`;
    }

    return html``;
  }

  private renderErrorMessage(): TemplateResult {
    if (this.errorMessage) {
      return html`<div
        class="il-input__error-message"
        data-test-iliad-input-error-message
      >
        ${this.alertIcon}
        ${this.errorMessage}
      </div>`;
    }

    return html``;
  }

  private renderInputButton(): TemplateResult {
    if (this.onClickInputButton) {
      return html`
        <il-button
          variant="secondary"
          ?disabled=${this.disabled}
          .onClick=${() => this._onClickInputButton()}
          data-test-iliad-input-button
          >${this.inputButtonLabel}</il-button
        >
      `;
    }

    return html``;
  }

  private _renderInputPrefix(): TemplateResult {
    if (this.inputPrefix) {
      return html`<div
          class="il-input__prefix"
          data-test-iliad-input-prefix
        >
        ${this.inputPrefix}
      </div>`
    }

    return html``
  }

  private _onClickInputButton(): void {
    this.onClickInputButton?.();
  }

  private _onChange(e: Event): void {
    this.value = (e.target as HTMLInputElement).value;
    if (this.onChange) {
      this.onChange(this.value);
    }
  }

  private _onBlur(e: Event): void {
    this.value = (e.target as HTMLInputElement).value;
    if (this.onBlur) {
      this.onBlur(this.value);
    }
  }

  private alertIcon = svg`<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
  <g id="Icon20/Alert">
  <path id="Vector" d="M9.12499 10.792H10.875V5.85399H9.12499V10.792ZM9.99999 14.188C10.236 14.188 10.441 14.108 10.615 13.948C10.7883 13.788 10.875 13.576 10.875 13.312C10.875 13.076 10.7883 12.8713 10.615 12.698C10.441 12.5247 10.236 12.438 9.99999 12.438C9.76399 12.438 9.55899 12.5247 9.38499 12.698C9.21166 12.8713 9.12499 13.076 9.12499 13.312C9.12499 13.576 9.21166 13.788 9.38499 13.948C9.55899 14.108 9.76399 14.188 9.99999 14.188ZM9.99999 18.333C8.84733 18.333 7.76399 18.1143 6.74999 17.677C5.73599 17.2397 4.85399 16.646 4.10399 15.896C3.35399 15.146 2.76033 14.264 2.32299 13.25C1.88566 12.236 1.66699 11.1527 1.66699 9.99999C1.66699 8.84733 1.88566 7.76399 2.32299 6.74999C2.76033 5.73599 3.35399 4.85399 4.10399 4.10399C4.85399 3.35399 5.73599 2.76033 6.74999 2.32299C7.76399 1.88566 8.84733 1.66699 9.99999 1.66699C11.1527 1.66699 12.236 1.88566 13.25 2.32299C14.264 2.76033 15.146 3.35399 15.896 4.10399C16.646 4.85399 17.2397 5.73599 17.677 6.74999C18.1143 7.76399 18.333 8.84733 18.333 9.99999C18.333 11.1527 18.1143 12.236 17.677 13.25C17.2397 14.264 16.646 15.146 15.896 15.896C15.146 16.646 14.264 17.2397 13.25 17.677C12.236 18.1143 11.1527 18.333 9.99999 18.333ZM9.99999 16.583C11.8193 16.583 13.3713 15.9407 14.656 14.656C15.9407 13.3713 16.583 11.8193 16.583 9.99999C16.583 8.18066 15.9407 6.62866 14.656 5.34399C13.3713 4.05933 11.8193 3.41699 9.99999 3.41699C8.18066 3.41699 6.62866 4.05933 5.34399 5.34399C4.05933 6.62866 3.41699 8.18066 3.41699 9.99999C3.41699 11.8193 4.05933 13.3713 5.34399 14.656C6.62866 15.9407 8.18066 16.583 9.99999 16.583Z" fill="currentColor"/>
  </g>
  </svg>
  `;
}
